<template>
  <div class="test-edit-container page-container bg-gradient-soft">
    <div class="container-padded py-5">
      <div class="d-flex justify-content-between align-items-center mb-5 animate__animated animate__fadeIn">
        <h1 class="mb-0 display-heading with-highlight">
          Test bearbeiten
        </h1>
        <div class="d-flex gap-3">
          <router-link :to="`/tests/${testId}/preview`" class="btn btn-info btn-with-icon btn-hover-effect">
            <span class="icon me-2"><i class="fas fa-eye"></i></span> Vorschau
          </router-link>
          <router-link to="/tests" class="btn btn-outline-secondary btn-with-icon btn-hover-effect">
            <span class="icon me-2"><i class="fas fa-arrow-left"></i></span> Zurück zur Testliste
          </router-link>
        </div>
      </div>
      
      <div v-if="loading" class="text-center py-5 content-loading">
        <div class="spinner-container">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Wird geladen...</span>
          </div>
        </div>
        <p class="mt-3 text-muted">Test wird geladen...</p>
      </div>
      
      <template v-else>
        <div class="row">
          <div class="col-12">
            <Card animateIn="fadeInUp" class="card-modern card-with-form shadow-lg">
              <template v-slot:header>
                <h4 class="card-title mb-0"><i class="fas fa-clipboard-list me-2"></i> Test-Informationen</h4>
              </template>
              
              <form @submit.prevent="saveTest" class="form-spaced py-4">
                <div class="mb-4">
                  <label for="testName" class="form-label label-emphasized">
                    <span class="icon icon-primary me-2"><i class="fas fa-pen"></i></span>
                    Test-Name
                  </label>
                  <input 
                    type="text" 
                    class="form-control form-control-lg with-focus-effect" 
                    id="testName" 
                    v-model="test.name" 
                    placeholder="z.B. Programmierkenntnisse Test"
                    required
                    :class="{ 'is-invalid': errors.name }"
                  >
                  <div class="invalid-feedback with-icon" v-if="errors.name">
                    <span class="icon icon-danger me-1"><i class="fas fa-exclamation-circle"></i></span>
                    {{ errors.name }}
                  </div>
                </div>
                
                 <div class="row mb-4">
                  <div class="col-md-6">
                    <div class="switch-card">
                      <div class="form-check form-switch">
                        <input 
                          class="form-check-input switch-lg switch-primary" 
                          type="checkbox" 
                          id="testActive" 
                          v-model="test.active"
                        >
                        <label class="form-check-label fw-medium" for="testActive">
                          <span class="icon icon-success me-2"><i class="fas fa-check-circle"></i></span>
                          Test aktiv
                        </label>
                 </div>

                 <!-- Zugeordnete Fragengruppen anzeigen und bearbeiten -->
                 <div class="mb-4">
                   <label class="form-label label-emphasized">
                     <span class="icon icon-primary me-2"><i class="fas fa-layer-group"></i></span>
                     Fragengruppen für diesen Test
                   </label>
                   <div class="alert alert-info mb-3">
                     <i class="fas fa-info-circle me-2"></i>
                     Wählen Sie Fragengruppen aus. Deren Fragen werden zusätzlich zu den Testfragen angezeigt.
                   </div>
                   <div v-if="availableGroupsLoading" class="text-muted">Lade Fragengruppen...</div>
                   <div v-else class="d-flex flex-column gap-2">
                     <div v-for="group in availableGroups" :key="group.id" class="form-check">
                       <input class="form-check-input" type="checkbox" :id="`g_${group.id}`" :value="group.id" v-model="selectedGroupIds" @change="saveSelectedGroups">
                       <label class="form-check-label" :for="`g_${group.id}`">
                         <span class="badge me-2" :style="{ backgroundColor: group.color, color: 'white' }">{{ group.name }}</span>
                         <small class="text-muted">{{ group.questions_count }} Fragen</small>
                       </label>
                     </div>
                   </div>
                 </div>
                      <div class="form-text ms-5 ps-2">
                        Wenn aktiviert, können Teilnehmer diesen Test ausfüllen.
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="switch-card">
                      <div class="form-check form-switch">
                        <input 
                          class="form-check-input switch-lg switch-primary" 
                          type="checkbox" 
                          id="showResults" 
                          v-model="test.show_results"
                        >
                        <label class="form-check-label fw-medium" for="showResults">
                          <span class="icon icon-info me-2"><i class="fas fa-chart-pie"></i></span>
                          Ergebnisse anzeigen
                        </label>
                      </div>
                      <div class="form-text ms-5 ps-2">
                        Wenn aktiviert, werden den Teilnehmern nach Abschluss des Tests ihre Ergebnisse angezeigt.
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="row mb-4">
                  <div class="col-md-6">
                    <div class="link-card">
                      <label class="form-label label-emphasized mb-2">
                        <span class="icon icon-info me-2"><i class="fas fa-link"></i></span>
                        Test-Link
                      </label>
                      <div class="input-group input-group-modern">
                        <span class="input-group-text bg-light border-end-0">
                          <i class="fas fa-globe"></i>
                        </span>
                        <input 
                          type="text" 
                          class="form-control border-start-0" 
                          :value="testLink" 
                          readonly
                        >
                        <button 
                          class="btn btn-primary" 
                          type="button" 
                          @click="copyTestLink"
                        >
                          <i class="fas fa-copy me-2"></i> Kopieren
                        </button>
                      </div>
                      <div class="form-text">
                        <span class="icon icon-muted me-1"><i class="fas fa-info-circle"></i></span>
                        Teilen Sie diesen Link mit Teilnehmern, um den Test durchzuführen.
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="mb-4">
                  <label for="testInstructions" class="form-label label-emphasized">
                    <span class="icon icon-warning me-2"><i class="fas fa-info-circle"></i></span>
                    Anweisungen (optional)
                  </label>
                  <textarea 
                    class="form-control with-focus-effect" 
                    id="testInstructions" 
                    v-model="test.instructions" 
                    rows="3"
                    placeholder="Geben Sie hier Anweisungen für die Teilnehmer ein..."
                  ></textarea>
                  <div class="form-text">
                    Diese Anweisungen werden den Teilnehmern vor dem Start des Tests angezeigt.
                  </div>
                </div>
                
                <div class="d-flex justify-content-between mt-5">
                  <button type="submit" class="btn btn-primary btn-lg btn-with-icon btn-glow-effect" :disabled="saveLoading">
                    <span v-if="saveLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    <span v-else class="icon me-2"><i class="fas fa-save"></i></span>
                    {{ saveLoading ? 'Wird gespeichert...' : 'Änderungen speichern' }}
                  </button>
                </div>
              </form>
            </Card>
          </div>
        </div>
        
        <div class="row mt-5">
          <div class="col-12">
            <Card 
              title="Test-Fragen" 
              animateIn="fadeInUp"
              class="card-modern card-with-table shadow-lg"
              :style="{ animationDelay: '0.2s' }"
            >
              <template v-slot:header>
                <div class="d-flex justify-content-between align-items-center">
                  <h4 class="card-title mb-0" style="margin-right: 1.2rem;"><i class="fas fa-question-circle me-2"></i> Test-Fragen</h4>
                  <div class="btn-group">
                    <button 
                      class="btn btn-primary btn-with-icon btn-hover-effect" 
                      @click="openAddQuestionModal('multiple_choice')"
                    >
                      <span class="icon me-2"><i class="fas fa-list-ul"></i></span> Multiple-Choice
                    </button>
                    <button 
                      class="btn btn-info btn-with-icon btn-hover-effect" 
                      @click="openAddQuestionModal('open')"
                    >
                      <span class="icon me-2"><i class="fas fa-font"></i></span> Offene Frage
                    </button>
                  </div>
                </div>
              </template>
              
              <div v-if="loadingQuestions" class="text-center py-4 card-loading-state">
                <div class="spinner-container mb-3">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Wird geladen...</span>
                  </div>
                </div>
                <p class="mt-2 text-muted">Fragen werden geladen...</p>
              </div>
              
              <div v-else>
                <div class="alert alert-info mb-4 alert-modern with-icon">
                  <span class="icon icon-info me-2"><i class="fas fa-info-circle"></i></span>
                  <span>Verwalten Sie die Fragen und ordnen Sie sie per Drag & Drop.</span>
                </div>
                
                <div class="mb-4">
                  <draggable
                    v-model="sortedQuestions"
                    handle=".drag-handle"
                    ghost-class="drag-ghost"
                    @end="saveQuestionsOrder"
                    class="questions-list-modern"
                    item-key="id"
                    :animation="200"
                    :delay="50"
                    :delay-on-touch-only="true"
                    :touch-start-threshold="5"
                  >
                    <template #item="{element, index}">
                      <div 
                        :key="element.id" 
                        class="question-card"
                        :class="{ 
                          'question-card-primary': element.type === 'multiple_choice', 
                          'question-card-info': element.type === 'open' 
                        }"
                      >
                        <div class="question-header d-flex align-items-center">
                          <div class="drag-handle me-3">
                            <div class="drag-handle-icon">
                              <i class="fas fa-grip-vertical"></i>
                            </div>
                          </div>
                          <div class="question-number-badge me-3">{{ index + 1 }}</div>
                          <span class="question-type-badge" 
                            :class="element.type === 'multiple_choice' ? 'badge-primary' : 'badge-info'">
                            {{ element.type === 'multiple_choice' 
                                ? (element.allow_multiple_answers ? 'Multiple-Choice (Mehrfachauswahl)' : 'Multiple-Choice (Einfachauswahl)') 
                                : 'Offene Frage' }}
                          </span>
                          <div class="ms-auto question-actions">
                            <button 
                              class="btn-action btn-action-edit" 
                              @click="editQuestion(element)"
                              title="Bearbeiten"
                            >
                              <i class="fas fa-edit"></i>
                            </button>
                            <button 
                              class="btn-action btn-action-delete" 
                              @click="showDeleteQuestion(element.id)"
                              title="Löschen"
                            >
                              <i class="fas fa-trash-alt"></i>
                            </button>
                          </div>
                        </div>
                        <div class="question-content mt-3">
                          <div class="question-text" v-html="element.description"></div>
                          
                          <div v-if="element.type === 'multiple_choice'" class="question-answers mt-3">
                            <div class="answers-title d-flex align-items-center mb-3">
                              <span class="icon icon-primary me-2"><i class="fas fa-list-check"></i></span>
                              <h5 class="mb-0">Antworten</h5>
                              <span class="badge badge-soft-secondary ms-2">mindestens 2 erforderlich</span>
                            </div>
                            
                            <div class="answers-container">
                              <div 
                                v-for="(answer, answerIndex) in element.answers" 
                                :key="`answer-${index}-${answerIndex}`" 
                                class="answer-item"
                                :class="{ 'answer-item-correct': answer.is_correct }"
                              >
                                <div v-if="answerIndex >= 2" class="optional-badge">Optional</div>
                                
                                <div class="answer-content">
                                  <div class="answer-checkbox">
                                    <span class="answer-status-icon">
                                      <i :class="answer.is_correct ? 'fas fa-check-circle' : 'far fa-circle'"></i>
                                    </span>
                                  </div>
                                  <div class="answer-text">
                                    {{ answer.text }}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </draggable>
                  
                  <div v-if="!sortedQuestions.length" class="text-center py-4 mt-3 card-empty-state">
                    <div class="empty-state-icon mb-4">
                      <i class="fas fa-question-circle"></i>
                    </div>
                    <h5 class="empty-state-title mb-3">Keine Fragen vorhanden</h5>
                    <p class="text-muted mb-4">Fügen Sie Fragen hinzu, um den Test zu erstellen.</p>
                    <div class="d-flex justify-content-center gap-3">
                      <button 
                        class="btn btn-primary btn-with-icon btn-hover-effect" 
                        @click="openAddQuestionModal('multiple_choice')"
                      >
                        <span class="icon me-2"><i class="fas fa-list-ul"></i></span>
                        Multiple Choice
                      </button>
                      <button 
                        class="btn btn-info btn-with-icon btn-hover-effect"
                        @click="openAddQuestionModal('open')"
                      >
                        <span class="icon me-2"><i class="fas fa-font"></i></span>
                        Offene Frage
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </template>
    </div>
    
    <!-- Modals hier einfügen -->
    <CreateQuestionModal 
      ref="createQuestionModal"
      :loading="questionLoading"
      :defaultPointsPerQuestion="defaultPointsPerQuestion"
      :questionType="currentQuestion.type"
      @save="saveQuestion"
      @save-and-add-next="saveAndAddNext"
      @reset="resetQuestionForm"
      @image-updated="handleQuestionImageUpdated"
      @image-removed="handleQuestionImageRemoved"
      @check-for-duplicates="checkForDuplicates"
    />
    
    <EditQuestionModal 
      ref="editQuestionModal"
      :loading="questionLoading"
      :defaultPointsPerQuestion="defaultPointsPerQuestion"
      @save="saveQuestion"
      @save-and-add-next="saveAndAddNext"
      @reset="resetQuestionForm"
      @image-updated="handleQuestionImageUpdated"
      @image-removed="handleQuestionImageRemoved"
      @check-for-duplicates="checkForDuplicates"
    />
    
    <!-- DeleteQuestionModal -->
    <div v-if="deleteModalVisible" class="custom-modal-wrapper">
      <div class="custom-modal-backdrop" @click="confirmDeleteQuestion(false)"></div>
      <div class="custom-modal">
        <div class="custom-modal-header">
          <h5 class="custom-modal-title">Frage löschen</h5>
          <button type="button" class="custom-modal-close" @click="confirmDeleteQuestion(false)">×</button>
        </div>
        <div class="custom-modal-body">
          <p>Sind Sie sicher, dass Sie diese Frage löschen möchten?</p>
          <div class="d-flex justify-content-end gap-2 mt-3">
            <button type="button" class="btn btn-outline-secondary" @click="confirmDeleteQuestion(false)">
              Abbrechen
            </button>
            <button type="button" class="btn btn-danger" @click="confirmDeleteQuestion(true)">
              <i class="fas fa-trash-alt me-2"></i>
              Löschen
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Card from '@/components/ui/Card.vue';
import draggable from 'vuedraggable';
import CreateQuestionModal from './modals/CreateQuestionModal.vue';
import EditQuestionModal from './modals/EditQuestionModal.vue';
import { useTestsStore } from '@/stores/tests';

export default {
  name: 'TestEdit',
  components: {
    Card,
    draggable,
    CreateQuestionModal,
    EditQuestionModal
  },
  setup() {
    const testsStore = useTestsStore();
    
    return {
      testsStore
    };
  },
  data() {
    return {
      test: {
        id: null,
        name: '',
        active: true,
        instructions: '',
        hash: '',
        show_results: true
      },
      loading: false,
      saveLoading: false,
      loadingQuestions: false,
      questionLoading: false,
      errors: {
        name: null
      },
      currentQuestion: {
        id: null,
        type: 'multiple_choice',
        description: '',
        points: 10,
        image_url: null,
        allow_multiple_answers: false,
        answer_1: '',
        answer_2: '',
        answer_3: '',
        answer_4: '',
        answer_5: '',
        answer_6: '',
        is_answer_1_correct: false,
        is_answer_2_correct: false,
        is_answer_3_correct: false,
        is_answer_4_correct: false,
        is_answer_5_correct: false,
        is_answer_6_correct: false
      },
      editingQuestion: false,
      questionToDelete: null,
      sortedQuestions: [],
      defaultPointsPerQuestion: 10,
      questionImagePreview: null,
      questionImageFile: null,
      singleCorrectAnswer: 0,
      deleteModalVisible: false,
      availableGroups: [],
      availableGroupsLoading: false,
      selectedGroupIds: []
    };
  },
  computed: {
    questions() {
      return this.testsStore.getQuestions || [];
    },
    currentTest() {
      return this.testsStore.getCurrentTest || null;
    },
    testId() {
      return this.$route.params.id;
    },
    testLink() {
      if (this.test && this.test.hash) {
        return `${window.location.origin}/test/${this.test.hash}`;
      }
      return '';
    },
    totalPoints() {
      if (!this.test || !this.test.questions || this.test.questions.length === 0) {
        return 0;
      }
      
      return this.test.questions.reduce((total, question) => {
        return total + (question.points || this.defaultPointsPerQuestion);
      }, 0);
    }
  },
  watch: {
    questions: {
      handler(newQuestions) {
        // Sicherstellen, dass die Fragen korrekt transformiert werden für die UI
        this.sortedQuestions = [...newQuestions].map(question => {
          // Erstelle eine UI-freundliche Version der Frage
          const transformedQuestion = { ...question };
          
          // Erstelle ein 'answers' Array für die UI
          transformedQuestion.answers = [];
          
          // Füge die Antworten hinzu, falls vorhanden
          for (let i = 1; i <= 6; i++) {
            if (question[`answer_${i}`] && question[`answer_${i}`].trim() !== '') {
              transformedQuestion.answers.push({
                text: question[`answer_${i}`],
                is_correct: question[`is_answer_${i}_correct`] === true
              });
            }
          }
          
          // Sortiere nach der angegebenen Reihenfolge oder nach ID
          return transformedQuestion;
        }).sort((a, b) => {
          // Primär nach sort_order, wenn verfügbar
          if (a.sort_order !== undefined && b.sort_order !== undefined) {
            return a.sort_order - b.sort_order;
          }
          // Sekundär nach ID
          return a.id - b.id;
        });
        
        console.log('Transformierte Fragen für UI:', this.sortedQuestions);
      },
      deep: true,
      immediate: true
    },
    currentTest(newTest) {
      if (newTest) {
        this.test = { ...newTest };
        // Übernehme zugewiesene Gruppen
        if (Array.isArray(newTest.question_groups)) {
          this.selectedGroupIds = newTest.question_groups.map(g => g.id);
        } else if (Array.isArray(newTest.questionGroups)) {
          this.selectedGroupIds = newTest.questionGroups.map(g => g.id);
        }
      }
    }
  },
  methods: {
    async loadAvailableGroups() {
      this.availableGroupsLoading = true;
      try {
        // Pinia-Store direkt importfrei über globalen http-Service
        const response = await this.$http.get('/api/question-groups');
        this.availableGroups = response.data || [];
      } catch (e) {
        this.availableGroups = [];
      } finally {
        this.availableGroupsLoading = false;
      }
    },
    async saveSelectedGroups() {
      try {
        await this.testsStore.updateTestData(this.testId, {
          ...this.test,
          question_group_ids: this.selectedGroupIds
        });
        this.$toast.success('Fragengruppen gespeichert');
        // Nach dem Speichern die Fragen erneut laden, damit Gruppenfragen sichtbar werden
        this.testsStore.invalidateCache();
        await this.loadQuestions();
      } catch (e) {
        this.$toast.error('Fragengruppen konnten nicht gespeichert werden');
      }
    },
    async loadTest() {
      this.loading = true;
      try {
        console.log("TestID:", this.testId);
        const test = await this.testsStore.fetchTest(this.testId);
        console.log("Geladener Test:", test);
        
        if (test) {
          // Manuell die Daten setzen und aktualisieren
          this.test = { ...test };
          console.log("Test in Komponente gesetzt:", this.test);
        } else {
          console.error("Test konnte nicht geladen werden");
          this.$toast.error("Test konnte nicht geladen werden");
        }
        
        await Promise.all([
          this.loadAvailableGroups(),
          this.loadQuestions()
        ]);
      } catch (error) {
        console.error("Fehler beim Laden des Tests:", error);
        this.$toast.error("Fehler beim Laden des Tests");
      } finally {
        this.loading = false;
      }
    },
    async loadQuestions() {
      this.loadingQuestions = true;
      try {
        const questions = await this.testsStore.fetchQuestions(this.testId);
        console.log("Geladene Fragen:", questions);
      } catch (error) {
        console.error("Fehler beim Laden der Fragen:", error);
        this.$toast.error("Fehler beim Laden der Fragen");
      } finally {
        this.loadingQuestions = false;
      }
    },
    async saveTest() {
      this.saveLoading = true;
      this.errors = { name: null };
      
      try {
        if (!this.test.name || this.test.name.trim() === '') {
          this.errors.name = "Bitte geben Sie einen Namen ein";
          this.$toast.error("Bitte füllen Sie alle erforderlichen Felder aus");
          return;
        }
        
        console.log("Speichere Test:", this.test);
        await this.testsStore.updateTestData(this.testId, this.test);
        this.$toast.success("Test wurde erfolgreich gespeichert");
      } catch (error) {
        console.error("Fehler beim Speichern des Tests:", error);
        this.$toast.error("Fehler beim Speichern des Tests");
      } finally {
        this.saveLoading = false;
      }
    },
    copyTestLink() {
      const el = document.createElement('textarea');
      el.value = this.testLink;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      this.$toast.success("Link in die Zwischenablage kopiert");
    },
    addQuestion() {
      this.currentQuestion = {
        id: null,
        type: 'multiple_choice',
        description: '',
        points: 10,
        image_url: null,
        allow_multiple_answers: false,
        answer_1: '',
        answer_2: '',
        answer_3: '',
        answer_4: '',
        answer_5: '',
        answer_6: '',
        is_answer_1_correct: false,
        is_answer_2_correct: false,
        is_answer_3_correct: false,
        is_answer_4_correct: false,
        is_answer_5_correct: false,
        is_answer_6_correct: false
      };
      this.editingQuestion = false;
      this.questionImagePreview = null;
      this.questionImageFile = null;
      this.$refs.createQuestionModal.show();
    },
    editQuestion(question) {
      console.log('TestEdit: Bearbeite Frage:', question);
      // Erstelle eine vollständige Kopie der Frage mit allen Eigenschaften
      this.currentQuestion = { ...question };
      
      // Stelle sicher, dass answer_X und is_answer_X_correct Eigenschaften korrekt kopiert werden
      for (let i = 1; i <= 6; i++) {
        if (question.answers && question.answers[i-1]) {
          this.currentQuestion[`answer_${i}`] = question.answers[i-1].text;
          this.currentQuestion[`is_answer_${i}_correct`] = question.answers[i-1].is_correct;
        }
      }
      
      this.editingQuestion = true;
      this.questionImagePreview = question.image_url;
      this.questionImageFile = null;
      
      console.log('TestEdit: Vollständige Frage für Modal:', this.currentQuestion);
      this.$refs.editQuestionModal.show(this.currentQuestion);
    },
    async saveQuestion(questionData, closeModal = true) {
      // Falls die Frage direkt vom Modal übergeben wird, verwende diese
      // anstatt prepareQuestionForSave() zu verwenden
      const question = questionData || this.prepareQuestionForSave();
      if (!question) return;
      
      this.questionLoading = true;
      
      try {
        console.log("Speichere Frage:", question);
        
        if (question.id) {
          // Aktualisiere vorhandene Frage
          const updatedQuestion = await this.testsStore.updateQuestion(
            this.testId,
            question.id,
            question
          );
          console.log("Aktualisierte Frage:", updatedQuestion);
        } else {
          // Erstelle neue Frage
          const createdQuestion = await this.testsStore.createQuestion(
            this.testId,
            question
          );
          console.log("Erstellte Frage:", createdQuestion);
        }
        
        // Fragen neu laden um sicherzustellen, dass wir die aktuellsten Daten haben
        await this.loadQuestions();
        
        if (closeModal) {
          if (this.editingQuestion) {
            this.$refs.editQuestionModal.hide();
          } else {
            this.$refs.createQuestionModal.hide();
          }
        }
      } catch (error) {
        console.error("Fehler beim Speichern der Frage:", error);
        this.$toast.error("Fehler beim Speichern der Frage");
      } finally {
        this.questionLoading = false;
      }
    },
    saveAndAddNext(questionData) {
      this.saveQuestion(questionData, false).then(() => {
        // Formular zurücksetzen für neue Frage
        this.resetQuestionForm();
        // Modal neu anzeigen mit sauberem Formular
        if (this.$refs.createQuestionModal) {
          this.$refs.createQuestionModal.show();
        }
      });
    },
    async deleteQuestion(questionId) {
      this.questionLoading = true;
      
      try {
        await this.testsStore.deleteQuestion(questionId);
        this.$toast.success("Frage erfolgreich entfernt");
        // Fragen neu laden
        await this.loadQuestions();
      } catch (error) {
        console.error("Fehler beim Löschen der Frage:", error);
        this.$toast.error("Frage konnte nicht gelöscht werden");
      } finally {
        this.questionLoading = false;
      }
    },
    showDeleteQuestion(questionId) {
      this.questionToDelete = questionId;
      this.deleteModalVisible = true;
    },
    confirmDeleteQuestion(confirm) {
      if (confirm && this.questionToDelete) {
        this.deleteQuestion(this.questionToDelete);
      }
      
      this.questionToDelete = null;
      this.deleteModalVisible = false;
    },
    resetQuestionForm() {
      this.currentQuestion = {
        id: null,
        type: 'multiple_choice',
        description: '',
        points: 10,
        image_url: null,
        allow_multiple_answers: false,
        answer_1: '',
        answer_2: '',
        answer_3: '',
        answer_4: '',
        answer_5: '',
        answer_6: '',
        is_answer_1_correct: false,
        is_answer_2_correct: false,
        is_answer_3_correct: false,
        is_answer_4_correct: false,
        is_answer_5_correct: false,
        is_answer_6_correct: false
      };
      this.questionImagePreview = null;
      this.questionImageFile = null;
    },
    async updateQuestionsOrder() {
      try {
        const orderedIds = this.sortedQuestions.map((question, index) => ({
          id: question.id,
          position: index
        }));
        
        await this.testsStore.updateQuestionsOrder(this.testId, orderedIds);
        this.$toast.success("Reihenfolge der Fragen erfolgreich aktualisiert");
      } catch (error) {
        console.error("Fehler beim Aktualisieren der Reihenfolge:", error);
        this.$toast.error("Fehler beim Aktualisieren der Reihenfolge");
      }
    },
    async handleQuestionImageUpdated(file) {
      try {
        // FormData für den Upload erstellen
        const formData = new FormData();
        formData.append('image', file);
        
        // Bild hochladen
        const response = await this.$http.post('/api/upload/question-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        
        // URL aus der Antwort extrahieren
        const imageUrl = response.data.url;
        
        // Vorschau und URL setzen
        this.questionImagePreview = imageUrl;
        this.questionImageFile = file;
        this.currentQuestion.image_url = imageUrl;
        
        // Auch in der defaultQuestion setzen für CreateQuestionModal
        if (this.$refs.createQuestionModal) {
          this.$refs.createQuestionModal.defaultQuestion.image_url = imageUrl;
        }
        
        // Auch in der editableQuestion setzen für EditQuestionModal
        if (this.$refs.editQuestionModal) {
          this.$refs.editQuestionModal.editableQuestion.image_url = imageUrl;
        }
        
        console.log('Bild erfolgreich hochgeladen:', imageUrl);
      } catch (error) {
        console.error('Fehler beim Bildupload:', error);
        this.$toast.error('Fehler beim Hochladen des Bildes');
      }
    },
    handleQuestionImageRemoved() {
      this.questionImagePreview = null;
      this.questionImageFile = null;
      this.currentQuestion.image_url = null;
    },
    prepareQuestionForSave() {
      // Kopie der Frage erstellen
      const question = { ...this.currentQuestion };
      
      // Sicherstellen, dass die Beschreibung ausgefüllt ist
      if (!question.description || question.description.trim() === '') {
        this.$toast.error('Bitte geben Sie eine Fragebeschreibung ein');
        return null;
      }
      
      // Sicherstellen, dass der Typ korrekt ist
      if (question.type !== 'multiple_choice' && question.type !== 'open') {
        question.type = 'multiple_choice';
      }
      
      // Punkte validieren
      if (!question.points || question.points < 1) {
        question.points = 10; // Standardwert
      }
      
      // Bei Multiple-Choice-Fragen sicherstellen, dass mindestens eine Antwort korrekt ist
      if (question.type === 'multiple_choice') {
        const hasCorrectAnswer = 
          question.is_answer_1_correct || 
          question.is_answer_2_correct || 
          question.is_answer_3_correct || 
          question.is_answer_4_correct || 
          question.is_answer_5_correct || 
          question.is_answer_6_correct;
        
        if (!hasCorrectAnswer) {
          this.$toast.error('Bitte markieren Sie mindestens eine korrekte Antwort');
          return null;
        }
        
        // Bei Single-Choice (allow_multiple_answers = false) sicherstellen, dass nur eine Antwort korrekt ist
        if (!question.allow_multiple_answers) {
          const correctAnswersCount = 
            (question.is_answer_1_correct ? 1 : 0) + 
            (question.is_answer_2_correct ? 1 : 0) + 
            (question.is_answer_3_correct ? 1 : 0) + 
            (question.is_answer_4_correct ? 1 : 0) + 
            (question.is_answer_5_correct ? 1 : 0) + 
            (question.is_answer_6_correct ? 1 : 0);
          
          if (correctAnswersCount > 1) {
            this.$toast.error('Bei Einfachauswahl (Single-Choice) darf nur eine Antwort korrekt sein');
            return null;
          }
        }
      }
      
      return question;
    },
    openAddQuestionModal(type = 'multiple_choice') {
      console.log('Öffne Add Question Modal mit Frage-Typ:', type);
      this.currentQuestion.type = type;
      if (this.$refs.createQuestionModal) {
        console.log('createQuestionModal Ref gefunden, zeige an');
        this.$refs.createQuestionModal.show();
      } else {
        console.warn('createQuestionModal Ref nicht gefunden!');
      }
    },
    setSingleCorrectAnswer(index) {
      // Alle Antworten auf falsch setzen
      for (let i = 1; i <= 6; i++) {
        this.currentQuestion[`is_answer_${i}_correct`] = false;
      }
      // Nur die ausgewählte Antwort auf wahr setzen
      this.currentQuestion[`is_answer_${index}_correct`] = true;
    },
    initializeSingleCorrectAnswer() {
      if (!this.currentQuestion.allow_multiple_answers) {
        // Finde die erste korrekte Antwort
        for (let i = 1; i <= 6; i++) {
          if (this.currentQuestion[`is_answer_${i}_correct`]) {
            this.singleCorrectAnswer = i;
            return;
          }
        }
        // Wenn keine korrekte Antwort gefunden wurde, setze auf 0
        this.singleCorrectAnswer = 0;
      }
    },
    checkForDuplicates(questionText) {
      if (!questionText || questionText.trim() === '') {
        return false;
      }
      
      const normalizedText = questionText.trim().toLowerCase();
      
      // Prüfe auf exakte Duplikate
      const exactDuplicates = this.sortedQuestions.filter(q => 
        q.description.trim().toLowerCase() === normalizedText
      );
      
      if (exactDuplicates.length > 0) {
        this.$toast.error('Eine Frage mit identischem Text existiert bereits.');
        return true;
      }
      
      // Prüfe auf ähnliche Fragen (einfache Implementierung)
      const similarQuestions = this.sortedQuestions.filter(q => {
        const existingText = q.description.trim().toLowerCase();
        const similarity = this.calculateSimilarity(normalizedText, existingText);
        return similarity > 0.8; // 80% Ähnlichkeit
      });
      
      if (similarQuestions.length > 0) {
        this.$toast.warning('Eine ähnliche Frage existiert bereits. Bitte überprüfen Sie den Text.');
        return true;
      }
      
      return false;
    },
    
    calculateSimilarity(text1, text2) {
      // Einfache Ähnlichkeitsberechnung basierend auf gemeinsamen Wörtern
      const words1 = text1.split(/\s+/).filter(word => word.length > 2);
      const words2 = text2.split(/\s+/).filter(word => word.length > 2);
      
      if (words1.length === 0 || words2.length === 0) {
        return 0;
      }
      
      const commonWords = words1.filter(word => words2.includes(word));
      const totalWords = Math.max(words1.length, words2.length);
      
      return commonWords.length / totalWords;
    }
  },
  async created() {
    // Lade den Test beim Erstellen
    try {
      await this.loadTest();
    } catch (error) {
      this.$toast.error('Fehler beim Laden des Tests');
      console.error(error);
    }
  }
};
</script>

<style lang="scss" scoped>
.test-edit-container {
  --card-radius: 16px;
  --shadow-sm: 0 5px 15px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 8px 20px rgba(0, 0, 0, 0.1);
  --shadow-lg: 0 12px 30px rgba(0, 0, 0, 0.15);
  --transition-base: all 0.25s ease;
  --border-width: 1px;
  --header-height: 60px;
  
  min-height: 100vh;
  padding: 2rem 0 3rem;
  background: var(--bg-color);

  .table {
    margin: 0;

    th {
      font-weight: 600;
      color: var(--text-color);
      padding: 1rem 1.5rem;
      border-bottom: 2px solid var(--border-color);
      white-space: nowrap;
    }

    td {
      padding: 1rem 1.5rem;
      vertical-align: middle;
      color: var(--text-color);
      border-bottom: 1px solid var(--border-color);
    }

    .data-row {
      transition: background-color 0.2s ease;

      &:hover {
        background-color: var(--hover-bg);
      }
    }
  }
}

.display-heading {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-color);
  
  &.with-highlight {
    position: relative;
    
    &:after {
      content: '';
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 60px;
      height: 4px;
      background: var(--primary-color);
      border-radius: 2px;
    }
  }
}

.icon-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: rgba(var(--bs-primary-rgb), 0.1);
  color: var(--bs-primary);
  
  &.icon-circle-primary {
    background: var(--primary-gradient);
    color: white;
    box-shadow: 0 5px 15px rgba(var(--bs-primary-rgb), 0.3);
  }
}

// Modern Card Styling
.card-modern {
  border: none;
  border-radius: var(--card-radius);
  overflow: hidden;
  transition: var(--transition-base);
  background-color: var(--card-bg);
  
  :deep(.card-header) {
    background-color: var(--card-bg);
    border-bottom: var(--border-width) solid var(--border-color);
    padding: 1.5rem;
    font-weight: 600;
    color: var(--text-color);
    .btn-group {
      .btn {
        border-radius: 0.5rem;
        padding: 0.5rem 1rem;
      }
      .btn-info {
        // Optional: Offene Frage weniger dominant gestalten
        // background-color: transparent;
        // border-color: var(--bs-info);
        // color: var(--bs-info);
        // &:hover {
        //    background-color: rgba(var(--bs-info-rgb), 0.1);
        // }
      }
    }
  }
  
  :deep(.card-body) {
    padding: 1.5rem;
    background-color: var(--card-bg);
    color: var(--text-color);
  }
}

// Loading State
.content-loading {
  .spinner-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 80px;
    height: 80px;
    margin: 0 auto;
    border-radius: 50%;
    background-color: rgba(var(--bs-primary-rgb), 0.05);
  }
}

// Form Styling
.form-spaced {
  margin: 1rem 0;
  
  .form-label {
    font-weight: 600;
    margin-bottom: 0.75rem;
    color: var(--text-color);
    font-size: 1rem;
    
    &.label-emphasized {
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }
  }
  
  .form-control, .form-select {
    border-radius: 10px;
    padding: 0.75rem 1rem;
    font-size: 1rem;
    transition: var(--transition-base);
    background-color: var(--input-bg);
    border-color: var(--border-color);
    color: var(--text-color);
    
    &:focus {
      border-color: var(--primary-color);
      box-shadow: 0 0 0 4px rgba(var(--primary-color-rgb), 0.15);
    }
  }
}

// Switch Card Styling
.switch-card {
  border-radius: 12px;
  padding: 1.5rem;
  transition: var(--transition-base);
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  
  &:hover {
    background-color: var(--hover-bg);
  }
  
  .form-check-label {
    color: var(--text-color);
    font-weight: 500;
  }
}

// Link Card Styling
.link-card {
  border-radius: 12px;
  padding: 1.5rem;
  transition: var(--transition-base);
  background-color: var(--card-bg);
  border: 1px solid var(--border-color);
  
  .input-group-modern {
    border-radius: 10px;
    overflow: hidden;
    
    .input-group-text {
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
      border-right: none;
      background-color: var(--input-bg);
      color: var(--text-color);
      border-color: var(--border-color);
    }
    
    .form-control {
      border-left: none;
      background-color: var(--input-bg);
      color: var(--text-color);
      border-color: var(--border-color);
    }
  }
}

// Button Styling
.btn-with-icon {
  display: flex;
  align-items: center;
  padding: 0.6rem 1.2rem;
  border-radius: 10px;
  font-weight: 500;
  transition: var(--transition-base);
  
  .icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

.btn-hover-effect {
  position: relative;
  overflow: hidden;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255, 255, 255, 0.1);
    z-index: -1;
    transform: translateY(100%);
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-sm);
    
    &:before {
      transform: translateY(0);
    }
  }
}

.btn-glow-effect {
  box-shadow: 0 5px 15px rgba(var(--bs-primary-rgb), 0.3);
  
  &:hover {
    box-shadow: 0 8px 20px rgba(var(--bs-primary-rgb), 0.4);
    transform: translateY(-2px);
  }
  
  &:active {
    transform: translateY(0);
  }
}

// Alert styling
.alert-modern {
  border-radius: 10px;
  border: 1px solid transparent;
  display: flex;
  align-items: center;
  
  &.with-icon {
    padding-left: 1rem;
    
    .icon {
      display: flex;
      align-items: center;
      font-size: 1.1rem;
    }
  }
}

// Questions List Styling
.questions-list-modern {
  margin-top: 1.5rem;
  
  .question-card {
    background-color: var(--card-bg);
    border-radius: 0.75rem;
    padding: 1.5rem;
    margin-bottom: 1.25rem;
    border: 1px solid var(--bs-border-color-translucent);
    box-shadow: var(--shadow-sm);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    position: relative;
    overflow: hidden;
    color: var(--text-color);

    border-left: 4px solid transparent;
    &.question-card-primary {
       border-left-color: var(--bs-primary);
    }
     &.question-card-info {
       border-left-color: var(--bs-info);
    }
    
    .question-header {
      border-bottom: 1px solid var(--bs-border-color-translucent);
      padding-bottom: 1rem;
      margin-bottom: 1rem;
      display: flex;
      align-items: center;
      gap: 0.75rem;

      .drag-handle {
        cursor: grab;
        color: var(--bs-gray-500);
        padding: 0.5rem;
        margin-left: -0.5rem;
        &:hover {
          color: var(--bs-primary);
        }
      }

      .question-number-badge {
        font-size: 0.8rem;
        font-weight: 600;
        background-color: var(--bs-gray-200);
        color: var(--bs-gray-700);
        min-width: 24px;
        height: 24px;
        border-radius: 50%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .question-type-badge {
        font-size: 0.8rem;
        padding: 0.3rem 0.6rem;
      }

      .question-actions {
        margin-left: auto;
        display: flex;
        gap: 0.5rem;

        .btn-action {
          background: none;
          border: none;
          color: var(--bs-gray-600);
          padding: 0.4rem;
          border-radius: 50%;
          width: 32px;
          height: 32px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background-color 0.2s ease, color 0.2s ease;

          &:hover {
            background-color: var(--bs-light);
            color: var(--bs-primary);
          }
          &.btn-action-delete:hover {
             background-color: rgba(var(--bs-danger-rgb), 0.1);
             color: var(--bs-danger);
          }
        }
      }
    }
    
    .question-content {
      .question-text {
        color: var(--text-color);
        margin-bottom: 1rem;
        line-height: 1.6;
      }
    }
    
    .answers-container {
      margin-top: 1rem;
      .answers-title {
         margin-bottom: 0.75rem !important;
         h5 {
           font-size: 0.95rem;
           font-weight: 600;
           color: var(--bs-gray-700);
         }
      }
      .answer-item {
        background-color: var(--bs-gray-100);
        color: var(--bs-gray-800);
        border: 1px solid var(--bs-border-color);
        padding: 0.75rem 1rem;
        border-radius: 0.5rem;
        margin-bottom: 0.5rem;
        position: relative;

        .optional-badge {
           position: absolute;
           top: 5px;
           right: 5px;
           font-size: 0.7rem;
           background-color: var(--bs-secondary);
           color: white;
           padding: 1px 4px;
           border-radius: 3px;
        }

        .answer-content {
           display: flex;
           align-items: center;
        }

        .answer-checkbox {
           margin-right: 0.75rem;
           .answer-status-icon {
              font-size: 1.1rem;
              color: var(--bs-gray-500);
           }
        }
        
        &.answer-item-correct {
          background-color: rgba(var(--bs-success-rgb), 0.1);
          border-color: rgba(var(--bs-success-rgb), 0.3);
          border-left-width: 4px;
          border-left-color: var(--bs-success);
          .answer-status-icon {
             color: var(--bs-success);
          }
          .answer-text {
            font-weight: 500;
          }
        }
      }
    }
  }
}

// Drag ghost styling
.drag-ghost {
  opacity: 0.7;
  background-color: rgba(var(--bs-primary-rgb), 0.1) !important;
  border: 2px dashed var(--bs-primary) !important;
  
  &:before {
    display: none;
  }
}

// Animation for list
.question-list-enter-active, .question-list-leave-active {
  transition: all 0.5s ease;
}

.question-list-enter-from, .question-list-leave-to {
  opacity: 0;
  transform: translateY(30px);
}

// Question Modal
.question-modal-modern {
  :deep(.modal-content) {
    border-radius: 16px;
    border: none;
    overflow: hidden;
  }
  
  :deep(.modal-header) {
    background: var(--primary-gradient);
    color: white;
    padding: 1.25rem 1.5rem;
    border-bottom: none;
    
    .close {
      color: white;
      opacity: 0.8;
      text-shadow: none;
      
      &:hover {
        opacity: 1;
      }
    }
  }
}

.modal-body-padded {
  padding: 1.5rem;
}

// Empty state
.card-empty-state {
  padding: 2rem 1rem;
  .empty-state-icon {
    width: 70px;
    height: 70px;
    font-size: 2rem;
    margin-bottom: 1.5rem !important;
  }
  
  .empty-state-title {
    font-weight: 600;
    color: var(--bs-gray-700);
    font-size: 1.2rem;
  }
  .d-flex.gap-3 > .btn {
    border-radius: 0.5rem;
    padding: 0.6rem 1.2rem;
  }
}

// Loading state
.card-loading-state {
  .spinner-container {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    margin: 0 auto;
    border-radius: 50%;
    background-color: rgba(var(--bs-primary-rgb), 0.05);
  }
}

// Delete modal
.delete-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 70px;
  height: 70px;
  margin: 0 auto;
  border-radius: 50%;
  background-color: rgba(var(--bs-danger-rgb), 0.1);
  color: var(--bs-danger);
  font-size: 2rem;
}

// Neues Modal-Styling
.custom-modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.custom-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
}

.custom-modal {
  position: relative;
  background-color: var(--card-bg);
  width: 90%;
  max-width: 500px;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  max-height: 90vh;
  overflow-y: auto;
  color: var(--text-color);
}

.custom-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #E63946, #F94144);
  color: white;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
}

.custom-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.custom-modal-close {
  background: transparent;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  margin: 0;
}

.custom-modal-body {
  padding: 1.5rem;
  color: var(--text-color);
}
</style> 